/**
 * Bridge PWA <-> wrapper nativo React Native.
 *
 * Quando la PWA gira dentro l'app nativa (apps/mobile WebView wrapper),
 * `window.vvfNativeBridge` viene esposto dal wrapper. La PWA puo' usarlo
 * per comunicare con la parte nativa (push, GPS, audio, ecc.).
 *
 * Il contratto dei messaggi (PWA -> wrapper) e degli eventi nativi
 * (wrapper -> PWA) e' definito in `@vvf/shared/bridge` — single source
 * of truth condivisa con apps/mobile.
 *
 * Vedi: apps/mobile/src/WebViewApp.tsx
 */
import {
  NATIVE_EVENT,
  type NativeAcceptDetail,
  type NativeRejectDetail,
  type NativeGpsTrackingStatusDetail,
  type NativeVolumesDumpDetail,
  type NativeLastBgDumpDetail,
} from '@vvf/shared/bridge';

interface VvfNativeBridge {
  postLogin: (accessToken: string, refreshToken: string, user: Record<string, unknown>) => void;
  postLogout: () => void;
  /** Item #1: la PWA chiede al nativo (owner) di rinnovare i token. */
  requestTokenRefresh?: () => void;
  postUserUpdate: (user: Record<string, unknown>) => void;
  postSelectiveResponse: (selectiveId: string, accepted: boolean) => void;
  startGpsTracking: (selectiveId: string) => void;
  stopGpsTracking: () => void;
  checkDndAccess?: () => Promise<boolean>;
  openDndSettings?: () => void;
  setBypassDndPreference?: (enable: boolean) => void;
  checkGpsEnabled?: () => Promise<boolean>;
  openGpsSettings?: () => void;
  setBypassVolumePreference?: (enable: boolean) => void;
  // Audio ownership unificata (P4): la PWA delega al wrapper.
  playSelectiveAlarm?: () => void;
  stopSelectiveAlarm?: () => void;
  playEndChime?: () => void;
  stopEndChime?: () => void;
  log: (payload: unknown) => void;
  isNativeApp: boolean;
  platform: 'android' | 'ios' | 'web';
}

declare global {
  interface Window {
    vvfNativeBridge?: VvfNativeBridge;
    IS_NATIVE_WRAPPER?: boolean;
    ReactNativeWebView?: { postMessage(message: string): void };
  }
}

export function isInNativeWrapper(): boolean {
  return typeof window !== 'undefined' && window.IS_NATIVE_WRAPPER === true;
}

/**
 * Chiede al wrapper nativo di selezionare e caricare un documento (per la
 * categoria data) in modo nativo, perche' l'upload via WebView fallisce su
 * Android. Il folderId (cartella di destinazione ODG/POS/Manuali) viene
 * inoltrato quando presente cosi' il nativo puo' caricare nel livello corrente;
 * assente = root (e per Rifornimenti, che resta un buffer piatto). Ritorna true
 * se la richiesta e' stata inoltrata (false in browser).
 */
export function requestNativeDocumentUpload(category: string, folderId?: string): boolean {
  if (isInNativeWrapper() && typeof window.ReactNativeWebView?.postMessage === 'function') {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'file:pick-upload', category, folderId }));
    return true;
  }
  return false;
}

/**
 * Chiede al wrapper nativo di SCATTARE/scegliere una foto (fotocamera o
 * galleria) e caricarla per la categoria data (Rifornimenti scontrini).
 * Ritorna true se inoltrato (false in browser).
 */
export function requestNativePhotoUpload(category: string, filename?: string): boolean {
  if (isInNativeWrapper() && typeof window.ReactNativeWebView?.postMessage === 'function') {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'file:pick-photo', category, filename }));
    return true;
  }
  return false;
}

/**
 * Avvia una chiamata al numero. In app passa dal bridge nativo (apre il dialer
 * affidabilmente: un <a href="tel:"> nel WebView Android spesso non funziona);
 * nel browser usa window.location. Il numero viene sanitizzato (solo cifre/+).
 */
export function dialPhone(rawNumber: string): void {
  const number = (rawNumber || '').replace(/[^\d+]/g, '');
  if (!number) return;
  if (isInNativeWrapper() && typeof window.ReactNativeWebView?.postMessage === 'function') {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'tel', number }));
  } else if (typeof window !== 'undefined') {
    window.location.href = `tel:${number}`;
  }
}

/** Si registra all'evento "upload documento completato" emesso dal nativo. */
export function onNativeDocumentUploaded(cb: () => void): () => void {
  const handler = (): void => cb();
  window.addEventListener(NATIVE_EVENT.DocumentUploaded, handler);
  return () => window.removeEventListener(NATIVE_EVENT.DocumentUploaded, handler);
}

export function notifyNativeLogin(accessToken: string, refreshToken: string, user: Record<string, unknown>): void {
  if (isInNativeWrapper() && window.vvfNativeBridge) {
    window.vvfNativeBridge.postLogin(accessToken, refreshToken, user);
  }
}

export function notifyNativeLogout(): void {
  if (isInNativeWrapper() && window.vvfNativeBridge) {
    window.vvfNativeBridge.postLogout();
  }
}

/**
 * Item #1 (single-owner refresh): in wrapper mode la PWA NON rinnova in
 * autonomia. Quando l'interceptor PWA prende 401, chiede al nativo (owner
 * unico) di rinnovare i token; il nativo poi sincronizza i nuovi token nel
 * localStorage della PWA via registerTokenRefreshSync. Ritorna true se la
 * richiesta e' stata inoltrata al wrapper (false in browser puro o se il
 * metodo non e' esposto).
 */
export function requestNativeTokenRefresh(): boolean {
  if (isInNativeWrapper() && window.vvfNativeBridge?.requestTokenRefresh) {
    window.vvfNativeBridge.requestTokenRefresh();
    return true;
  }
  return false;
}

/**
 * Sincronizza l'oggetto utente (es. dopo cambio settings come bypassVolume)
 * col SecureStore nativo. Senza questo, al cold start del wrapper viene
 * iniettato in localStorage il 'vvf.user' del primo login (con bypassVolume
 * di default false), e la checkbox tornava deselezionata.
 */
export function notifyNativeUserUpdate(user: Record<string, unknown>): void {
  if (isInNativeWrapper() && window.vvfNativeBridge?.postUserUpdate) {
    window.vvfNativeBridge.postUserUpdate(user);
  }
}

export function notifyNativeSelectiveResponse(selectiveId: string, accepted: boolean): void {
  if (isInNativeWrapper() && window.vvfNativeBridge) {
    window.vvfNativeBridge.postSelectiveResponse(selectiveId, accepted);
  }
}

/**
 * Chiede al wrapper nativo di iniziare il GPS tracking per la selettiva.
 * Il task gira come foreground service Android: continua anche con app
 * chiusa, posta lat/lng al backend ogni ~10s. Ritorna true se il wrapper
 * ha ricevuto la richiesta (non garantisce che i permessi siano OK).
 */
export function notifyNativeStartGpsTracking(selectiveId: string): boolean {
  if (isInNativeWrapper() && window.vvfNativeBridge) {
    window.vvfNativeBridge.startGpsTracking(selectiveId);
    return true;
  }
  return false;
}

export function notifyNativeStopGpsTracking(): void {
  if (isInNativeWrapper() && window.vvfNativeBridge) {
    window.vvfNativeBridge.stopGpsTracking();
  }
}

/**
 * Chiede al wrapper se il permesso Android "Non disturbare access" e' concesso.
 * Necessario per applicare bypassDnd: true sul channel selettiva, altrimenti
 * Android lo downgrada silenziosamente. Ritorna true su web (no-op).
 */
export async function checkNativeDndAccess(): Promise<boolean> {
  if (!isInNativeWrapper() || !window.vvfNativeBridge?.checkDndAccess) return true;
  return window.vvfNativeBridge.checkDndAccess();
}

/** Apre la pagina Settings Android dove l'utente puo' concedere DnD access. */
export function openNativeDndSettings(): void {
  if (isInNativeWrapper() && window.vvfNativeBridge?.openDndSettings) {
    window.vvfNativeBridge.openDndSettings();
  }
}

/**
 * Ricrea il channel selettiva nativo col flag bypassDnd richiesto.
 * Va chiamato quando l'utente cambia il toggle 'Bypass volume / DnD' nelle
 * impostazioni: i campi channel Android sono immutabili dopo creazione,
 * quindi il wrapper delete + recreate.
 */
export function setNativeBypassDndPreference(enable: boolean): void {
  if (isInNativeWrapper() && window.vvfNativeBridge?.setBypassDndPreference) {
    window.vvfNativeBridge.setBypassDndPreference(enable);
  }
}

/** True se il GPS del device e' acceso. Web: usa il browser geolocation API. */
export async function checkNativeGpsEnabled(): Promise<boolean> {
  if (isInNativeWrapper() && window.vvfNativeBridge?.checkGpsEnabled) {
    return window.vvfNativeBridge.checkGpsEnabled();
  }
  // Browser: non possiamo distinguere GPS-off da permesso-negato. Tentativo
  // breve di getCurrentPosition e ritorno true se ha successo.
  return new Promise(resolve => {
    if (!navigator.geolocation) return resolve(false);
    navigator.geolocation.getCurrentPosition(
      () => resolve(true),
      () => resolve(false),
      { timeout: 3000, enableHighAccuracy: false },
    );
  });
}

/** Apre la pagina Settings location del device (Android). No-op iOS/web. */
export function openNativeGpsSettings(): void {
  if (isInNativeWrapper() && window.vvfNativeBridge?.openGpsSettings) {
    window.vvfNativeBridge.openGpsSettings();
  }
}

/**
 * Configura il bypass suoneria nativo: la selettiva suona sempre sulla
 * SUONERIA (USAGE_NOTIFICATION_RINGTONE, spec Bryan 2026-06-07); con
 * enable=true il volume della suoneria viene alzato al massimo durante la
 * selettiva. Implementato dal native module VvfAudio nel wrapper.
 */
export function setNativeBypassVolumePreference(enable: boolean): void {
  if (isInNativeWrapper() && window.vvfNativeBridge?.setBypassVolumePreference) {
    window.vvfNativeBridge.setBypassVolumePreference(enable);
  }
}

/**
 * Listener per quando il wrapper nativo segnala "accetta selettiva" o "rifiuta"
 * (dal modal nativo che parte alla ricezione della push FCM).
 */
// Audio ownership unificata (P4 problemi strutturali): la PWA dentro
// wrapper chiama il bridge invece di produrre audio direttamente. In
// browser puro queste funzioni sono no-op (isInNativeWrapper=false) e
// l'audio viene prodotto da selectiveAlarm.ts (HTMLAudioElement).
export function playNativeSelectiveAlarm(): boolean {
  if (isInNativeWrapper() && window.vvfNativeBridge?.playSelectiveAlarm) {
    window.vvfNativeBridge.playSelectiveAlarm();
    return true;
  }
  return false;
}

export function stopNativeSelectiveAlarm(): boolean {
  if (isInNativeWrapper() && window.vvfNativeBridge?.stopSelectiveAlarm) {
    window.vvfNativeBridge.stopSelectiveAlarm();
    return true;
  }
  return false;
}

export function playNativeEndChime(): boolean {
  if (isInNativeWrapper() && window.vvfNativeBridge?.playEndChime) {
    window.vvfNativeBridge.playEndChime();
    return true;
  }
  return false;
}

export function stopNativeEndChime(): boolean {
  if (isInNativeWrapper() && window.vvfNativeBridge?.stopEndChime) {
    window.vvfNativeBridge.stopEndChime();
    return true;
  }
  return false;
}

/**
 * Listener per l'esito di startBackgroundGps lato wrapper (audit #4).
 * Permette alla PWA di mostrare un warning all'utente quando il tracking
 * non e' partito (permesso negato, errore).
 */
export function onNativeGpsTrackingStatus(
  callback: (active: boolean, reason?: string) => void,
): () => void {
  if (typeof window === 'undefined') return () => { /* noop */ };
  const handler = (e: Event) => {
    const detail = (e as CustomEvent<NativeGpsTrackingStatusDetail>).detail;
    if (detail) callback(detail.active, detail.reason);
  };
  window.addEventListener(NATIVE_EVENT.GpsTrackingStatus, handler);
  return () => window.removeEventListener(NATIVE_EVENT.GpsTrackingStatus, handler);
}

/**
 * Diagnostica audio: chiede al wrapper il dump di volumi/ringerMode/permission
 * e ritorna i dati. Risponde con null se non siamo in wrapper o timeout 3s.
 * Permette alla PWA di mostrare la diagnostica in un dialog senza richiedere
 * chrome://inspect o adb logcat.
 */
export function requestNativeVolumesDump(): Promise<NativeVolumesDumpDetail | null> {
  return new Promise(resolve => {
    if (!isInNativeWrapper() || typeof window === 'undefined' || !window.vvfNativeBridge) {
      resolve(null);
      return;
    }
    let settled = false;
    const handler = (e: Event) => {
      if (settled) return;
      settled = true;
      window.removeEventListener(NATIVE_EVENT.VolumesDump, handler);
      const detail = (e as CustomEvent<NativeVolumesDumpDetail>).detail;
      resolve(detail ?? null);
    };
    window.addEventListener(NATIVE_EVENT.VolumesDump, handler);
    // Trigger via dumpVolumes() (espone postMessage('debug:dump-volumes')).
    const bridge = window.vvfNativeBridge as { dumpVolumes?: () => void };
    bridge.dumpVolumes?.();
    setTimeout(() => {
      if (settled) return;
      settled = true;
      window.removeEventListener(NATIVE_EVENT.VolumesDump, handler);
      resolve(null);
    }, 3000);
  });
}

/**
 * Recupera l'ULTIMO snapshot diagnostico catturato dal wrapper all'arrivo
 * di una FCM selective.created — anche se l'app era in BACKGROUND warm
 * quando l'evento e' arrivato. Permette di capire perche' "in bg non suona"
 * leggendo lo stato esatto al momento del trigger.
 *
 * Ritorna { ts: null, data: null } se nessuna FCM e' ancora arrivata da
 * quando l'app gira. Timeout 3s.
 */
export function requestLastBgDump(): Promise<NativeLastBgDumpDetail | null> {
  return new Promise(resolve => {
    if (!isInNativeWrapper() || typeof window === 'undefined' || !window.vvfNativeBridge) {
      resolve(null);
      return;
    }
    let settled = false;
    const handler = (e: Event) => {
      if (settled) return;
      settled = true;
      window.removeEventListener(NATIVE_EVENT.LastBgDump, handler);
      const detail = (e as CustomEvent<NativeLastBgDumpDetail>).detail;
      resolve(detail ?? null);
    };
    window.addEventListener(NATIVE_EVENT.LastBgDump, handler);
    const bridge = window.vvfNativeBridge as { getLastBgDump?: () => void };
    bridge.getLastBgDump?.();
    setTimeout(() => {
      if (settled) return;
      settled = true;
      window.removeEventListener(NATIVE_EVENT.LastBgDump, handler);
      resolve(null);
    }, 3000);
  });
}

export function onNativeSelectiveResponse(
  callback: (selectiveId: string, accepted: boolean) => void
): () => void {
  if (typeof window === 'undefined') return () => { /* noop */ };
  const onAccept = (e: Event) => {
    const detail = (e as CustomEvent<NativeAcceptDetail>).detail;
    if (detail?.selectiveId) callback(detail.selectiveId, true);
  };
  const onReject = (e: Event) => {
    const detail = (e as CustomEvent<NativeRejectDetail>).detail;
    if (detail?.selectiveId) callback(detail.selectiveId, false);
  };
  window.addEventListener(NATIVE_EVENT.Accept, onAccept);
  window.addEventListener(NATIVE_EVENT.Reject, onReject);
  return () => {
    window.removeEventListener(NATIVE_EVENT.Accept, onAccept);
    window.removeEventListener(NATIVE_EVENT.Reject, onReject);
  };
}
